import { CORE_COLORS, CORE_NAMES, allocateBotColor, allocateColor } from "./palette.ts";
import { createRandom, randomBetween, randomInt, type RandomState } from "./random.ts";

/**
 * A human is a participant whose input arrives over the wire; a bot is a
 * participant whose input is produced by the AI. Nothing else about them differs,
 * which is what lets one simulation serve both.
 */
export type ParticipantKind = "human" | "bot";

export type ParticipantId = string;

export type Participant = {
  id: ParticipantId;
  kind: ParticipantKind;
  name: string;
  color: string;
  x: number;
  y: number;
  vx: number;
  vy: number;
  orbs: number;
  score: number;
  alive: boolean;
  /** Seconds until this participant may fire again. */
  attackCooldown: number;
  shield: number;
  shieldCooldown: number;
  boostRemaining: number;
  /** Seconds until a dead participant returns; humans currently never respawn. */
  respawn: number;
  /**
   * What a bot has committed to pursuing, and until when. Server-side only: it is
   * never serialised, because nobody needs to know what a bot is thinking.
   */
  bot?: { kind: string; id: string; until: number };
  /**
   * Seconds until a closed ring can be spent again. Nothing records *that* the
   * orbit is charged — that is read off the orb count — but a surge has to cost
   * something, or the orbs it steals come straight back and arm it again.
   */
  surgeCooldown: number;
  /**
   * Seconds of immunity after arriving. Without it a core that spawns inside a
   * firefight is shot before it can move. Attacking gives it up — otherwise it is
   * five seconds of free hunting.
   */
  spawnProtection: number;
  /** Steering memory, only meaningful for AI-driven participants. */
  targetX: number;
  targetY: number;
};

export type Energy = {
  id: number;
  owner: ParticipantId;
  x: number;
  y: number;
  color: string;
  size: number;
  phase: number;
};

export type Shot = {
  active: boolean;
  id: number;
  owner: ParticipantId;
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  traveled: number;
  returning: boolean;
  stolen: boolean;
  /** The ring this was fired from, 0 for an ordinary orb. Decides what it takes. */
  surge: number;
  /** How many orbs this shot took, so the same number comes home. */
  taken: number;
  color: string;
};

export type Spark = {
  active: boolean;
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  color: string;
};

export type GameData = {
  /** Every simulation-visible random draw comes from here, never Math.random(). */
  random: RandomState;
  /** Everyone in the arena, humans and bots alike, in join order. */
  participants: Participant[];
  energy: Energy[];
  /** Ceiling for the energy field; elimination drops recycle instead of growing it. */
  energyCap: number;
  energyDropIndex: number;
  shots: Shot[];
  sparks: Spark[];
  shotPool: Shot[];
  sparkPool: Spark[];
  sparkOverwriteIndex: number;
  stationCycle: number;
  /** Orbs each core has taken from the current storm; capped by `stationBonus`. */
  stormTaken: Map<ParticipantId, number>;
  /** How long each core has stood in the storm since its last orb. Resets on leaving. */
  stormDrip: Map<ParticipantId, number>;
  /** Edge-detects the storm activation so the announcement fires once per cycle. */
  stationsWereActive: boolean;
  /** Which announcement has already been made for the current storm. */
  stormPhase: "closed" | "warning" | "open";
  /**
   * Who the room is hunting, or null when nobody is far enough ahead to be worth
   * marking. Derived from scores every tick — see bounty.ts — and put on the wire
   * so the crown a client draws is the one the server is paying double for.
   */
  bounty: ParticipantId | null;
  /** Seconds the mark stays put before it may change hands again. */
  bountyHold: number;
  width: number;
  height: number;
  /** 0 means the match never ends; `timeLeft` is meaningless then. */
  matchDuration: number;
  timeLeft: number;
  /** Seconds since the match began. Counts up whether or not there is a clock. */
  elapsed: number;
  lastTime: number;
  nextId: number;
  running: boolean;
};

/** Id of the human a local client controls and renders the camera around. */
export const LOCAL_PLAYER_ID: ParticipantId = "player";
export const PLAYER_NAME = "NOVA";
export const DEFAULT_PLAYER_COLOR = "#36d8ff";
/**
 * A core arrives empty and has to go and find its ammunition. Nothing can be fired,
 * shielded or boosted without an orb, so the first thing anybody does in an arena
 * is collect — which is also the first thing the game wants to teach.
 */
export const STARTING_ORBS = 0;
/** Seats in one arena. The palette holds exactly this many distinct colours. */
export const ROOM_CAPACITY = 20;
/** Immunity granted on arrival, in seconds. */
export const SPAWN_PROTECTION_SECONDS = 5;

export type Leader = { id: ParticipantId; name: string; score: number; color: string };
export type AimPoint = { x: number; y: number; active: boolean };

/**
 * One seat per entry, names paired with the shared palette so a full room never has
 * two cores of the same colour. See `palette.ts` for why that is a rule, not taste.
 */
export const BOT_PRESETS = CORE_NAMES.map((name, index) => [name, CORE_COLORS[index]] as const);

export function botId(index: number): ParticipantId {
  return `bot:${index + 1}`;
}

/** Bot colour that never collides with a colour already on the field. */
export function getBotColor(index: number, ...taken: string[]) {
  return allocateBotColor(index, taken);
}

export function distance(ax: number, ay: number, bx: number, by: number) {
  return Math.hypot(ax - bx, ay - by);
}

export function clamp(value: number, min: number, max: number) {
  return Math.max(min, Math.min(max, value));
}

function baseParticipant(overrides: Partial<Participant> & Pick<Participant, "id" | "kind" | "name" | "color">): Participant {
  return {
    x: 0,
    y: 0,
    vx: 0,
    vy: 0,
    orbs: STARTING_ORBS,
    score: 0,
    alive: true,
    attackCooldown: 0,
    shield: 0,
    shieldCooldown: 0,
    boostRemaining: 0,
    respawn: 0,
    surgeCooldown: 0,
    spawnProtection: SPAWN_PROTECTION_SECONDS,
    targetX: 0,
    targetY: 0,
    ...overrides,
  };
}

/** Places a participant somewhere sane inside the arena. */
export function spawnPosition(width: number, height: number, random: RandomState) {
  return {
    x: randomBetween(random, 90, 90 + Math.max(120, width - 180)),
    y: randomBetween(random, 110, 110 + Math.max(160, height - 250)),
  };
}

export function createHuman(
  id: ParticipantId,
  name: string,
  color: string,
  width: number,
  height: number,
  random: RandomState = createRandom(1),
): Participant {
  const spawn = spawnPosition(width, height, random);
  return baseParticipant({ id, kind: "human", name, color, x: spawn.x, y: spawn.y, targetX: spawn.x, targetY: spawn.y });
}

export function createBot(
  index: number,
  width: number,
  height: number,
  fireInterval = 0.5,
  random: RandomState = createRandom(index + 1),
  taken: readonly string[] = [],
): Participant {
  const preset = BOT_PRESETS[index % BOT_PRESETS.length];
  const spawn = spawnPosition(width, height, random);
  return baseParticipant({
    id: botId(index),
    kind: "bot",
    name: preset[0],
    color: allocateBotColor(index, taken),
    x: spawn.x,
    y: spawn.y,
    targetX: randomBetween(random, 0, width),
    targetY: randomBetween(random, 0, height),
    score: 150 + randomInt(random, 350),
    attackCooldown: fireInterval,
  });
}

/**
 * Energy is sized per participant, not per arena. With a flat field a 20-player room
 * leaves each core roughly 1/20th of the map in its own colour, and growing becomes
 * a matter of luck. Giving every participant the same quota keeps "collect your own"
 * playable whether two people or twenty are in the room.
 */
export const ENERGY_PER_PARTICIPANT = 22;
export const MIN_ENERGY = 120;
export const MAX_ENERGY = 560;

export function energyCountFor(participantCount: number) {
  return Math.max(MIN_ENERGY, Math.min(MAX_ENERGY, participantCount * ENERGY_PER_PARTICIPANT));
}

export function createEnergy(
  id: number,
  width: number,
  height: number,
  owners: ReadonlyArray<{ id: ParticipantId; color: string }>,
  random: RandomState = createRandom(id + 1),
): Energy {
  const owner = owners.length > 0 ? owners[randomInt(random, owners.length)] : { id: LOCAL_PLAYER_ID, color: DEFAULT_PLAYER_COLOR };
  return {
    id,
    owner: owner.id,
    x: randomBetween(random, 28, 28 + Math.max(40, width - 56)),
    y: randomBetween(random, 82, 82 + Math.max(80, height - 150)),
    color: owner.color,
    size: randomBetween(random, 3.5, 7),
    phase: randomBetween(random, 0, Math.PI * 2),
  };
}

export type CreateGameOptions = {
  width: number;
  height: number;
  matchDuration?: number;
  fireInterval?: number;
  now?: number;
  seed?: number;
  /** Humans present at kickoff. A server starts empty and adds them as they join. */
  humans?: ReadonlyArray<{ id: ParticipantId; name: string; color: string }>;
  botCount?: number;
};

export function createGame(options: CreateGameOptions): GameData {
  const {
    width,
    height,
    matchDuration = 90,
    fireInterval = 0.5,
    now = 0,
    seed = 1,
    humans = [{ id: LOCAL_PLAYER_ID, name: PLAYER_NAME, color: DEFAULT_PLAYER_COLOR }],
  } = options;
  // Bots fill the seats humans have not taken, so a room is always ROOM_CAPACITY
  // cores and never needs a colour the palette does not have.
  const botCount = options.botCount ?? Math.max(0, ROOM_CAPACITY - humans.length);

  const random = createRandom(seed);
  // Colours are handed out once, room-wide. Assigning them per participant lets two
  // cores land on the same colour, which breaks the "collect your own" rule.
  const taken: string[] = [];
  const participants: Participant[] = [];

  for (const human of humans) {
    // Honour the colour the caller asked for unless the room already holds it.
    const wanted = human.color?.toLowerCase();
    const color = wanted && !taken.includes(wanted) ? human.color : allocateColor(taken);
    taken.push(color.toLowerCase());
    participants.push(createHuman(human.id, human.name, color, width, height, random));
  }

  for (let index = 0; index < botCount; index += 1) {
    const bot = createBot(index, width, height, fireInterval, random, taken);
    taken.push(bot.color.toLowerCase());
    participants.push(bot);
  }

  let nextId = 1;
  const owners = participants.map((participant) => ({ id: participant.id, color: participant.color }));
  // An empty arena (a server room before anyone joins) has nobody to own energy;
  // `rebalanceEnergy` seeds the field on the first join.
  const energyCount = owners.length === 0 ? 0 : energyCountFor(participants.length);
  // Round-robin rather than random, so shares start exactly level.
  const energy = Array.from({ length: energyCount }, (_unused, index) =>
    createEnergy(nextId++, width, height, [owners[index % owners.length]], random));

  return {
    random,
    participants,
    energy,
    energyCap: energyCount + 120,
    energyDropIndex: 0,
    shots: [],
    sparks: [],
    shotPool: [],
    sparkPool: [],
    sparkOverwriteIndex: 0,
    stationCycle: -1,
    stormTaken: new Map(),
    stormDrip: new Map(),
    stationsWereActive: false,
    stormPhase: "closed",
    bounty: null,
    bountyHold: 0,
    width,
    height,
    matchDuration,
    timeLeft: matchDuration,
    elapsed: 0,
    lastTime: now,
    nextId,
    running: true,
  };
}

/**
 * Keeps every participant's share of the field equal as the roster changes.
 *
 * Called when someone joins or leaves. Orphaned particles (their owner left) are
 * handed to whoever currently has the fewest, and the field is resized so each core
 * still gets `ENERGY_PER_PARTICIPANT`. Without this, a player who joins a running
 * match finds none of their own colour and cannot grow at all.
 */
export function rebalanceEnergy(game: GameData) {
  const roster = game.participants;
  if (roster.length === 0) return;

  const byId = new Map(roster.map((participant) => [participant.id, participant]));
  const counts = new Map<ParticipantId, number>(roster.map((participant) => [participant.id, 0]));
  const poorest = () => {
    let best = roster[0];
    for (const participant of roster) {
      if ((counts.get(participant.id) ?? 0) < (counts.get(best.id) ?? 0)) best = participant;
    }
    return best;
  };
  const richest = () => {
    let best = roster[0];
    for (const participant of roster) {
      if ((counts.get(participant.id) ?? 0) > (counts.get(best.id) ?? 0)) best = participant;
    }
    return best;
  };
  const assign = (item: Energy, owner: Participant) => {
    counts.set(item.owner, Math.max(0, (counts.get(item.owner) ?? 1) - 1));
    item.owner = owner.id;
    item.color = owner.color;
    // Relocate too, so a particle never visibly changes colour under someone's nose.
    const spot = createEnergy(item.id, game.width, game.height, [{ id: owner.id, color: owner.color }], game.random);
    item.x = spot.x;
    item.y = spot.y;
    counts.set(owner.id, (counts.get(owner.id) ?? 0) + 1);
  };

  // 1. Adopt particles whose owner has left, and repair colours after a respawn.
  for (const item of game.energy) {
    const owner = byId.get(item.owner);
    if (owner) {
      item.color = owner.color;
      counts.set(item.owner, (counts.get(item.owner) ?? 0) + 1);
    } else {
      counts.set(item.owner, 0);
      assign(item, poorest());
    }
  }

  // 2. Resize the field for the new roster.
  const target = energyCountFor(roster.length);
  game.energyCap = target + 120;
  while (game.energy.length < target) {
    const heir = poorest();
    game.energy.push(createEnergy(game.nextId++, game.width, game.height, [{ id: heir.id, color: heir.color }], game.random));
    counts.set(heir.id, (counts.get(heir.id) ?? 0) + 1);
  }
  while (game.energy.length > target) {
    const donor = richest();
    const at = game.energy.findIndex((item) => item.owner === donor.id);
    if (at < 0) break;
    game.energy.splice(at, 1);
    counts.set(donor.id, (counts.get(donor.id) ?? 1) - 1);
  }

  // 3. Level the shares. Resizing alone leaves a joiner with nothing when the field
  //    was already at target, which is exactly the case that makes them unable to
  //    grow. Hand particles from the most-supplied core to the least until the gap
  //    is at most one.
  let guard = game.energy.length * 2;
  while (guard-- > 0) {
    const from = richest();
    const to = poorest();
    if ((counts.get(from.id) ?? 0) - (counts.get(to.id) ?? 0) <= 1) break;
    const item = game.energy.find((candidate) => candidate.owner === from.id);
    if (!item) break;
    assign(item, to);
  }
}

export function findParticipant(game: GameData, id: ParticipantId): Participant | undefined {
  return game.participants.find((participant) => participant.id === id);
}

export function energyOwnerPool(game: GameData) {
  return game.participants.map((participant) => ({ id: participant.id, color: participant.color }));
}
